import { fanYMax } from '../chart/fanDomain'
import type { SimResult } from '../engine/types'
import type { CardTheme } from './palette'

/**
 * Everything the share card is allowed to know.
 *
 * **The omissions are the design.** `SimResult` carries `medianCorpusAtTarget` and
 * `requiredAtTarget`, and the on-screen headline shows both. The card does not,
 * because it gets posted to a public feed and those two numbers are somebody's
 * actual savings and actual cost of living. Rather than writing that down as a
 * comment in the drawing code and hoping, the constraint is expressed as a type:
 * `paintCard` accepts only a `CardModel`, so drawing a rupee figure would require
 * widening this interface first — a change nobody makes by accident, and one that
 * has to get past this doc comment.
 *
 * The risk-profile label is absent for a related but distinct reason. In the app
 * the allocation picker sits directly above "Illustrative mixes to simulate — not
 * recommended portfolios". On a card that qualifier is gone, and "Aggressive"
 * printed beside a success probability reads much closer to a recommended
 * portfolio than the app ever does. It also tells a viewer who has not seen the
 * mixes almost nothing. So it is not passed here at all.
 */
export interface CardModel {
  /**
   * The settled probability, 0..1 — or null.
   *
   * Null is the link-preview case. The static og:image is a generic asset for a
   * URL nobody has personalised yet, so a probability computed from the default
   * plan would be a specific claim about a stranger's finances that happens to be
   * whatever the defaults produce. It also renders about 120px tall in a Reddit
   * or WhatsApp preview, where no disclaimer is legible at all — which is exactly
   * the prominence problem the qualification exists to solve. The landscape
   * arrangement draws the tagline instead.
   */
  probability: number | null
  targetAge: number
  theme: CardTheme

  /** Age axis. Absolute ages, not years from now. */
  ages: Float32Array
  p10: Float32Array
  p25: Float32Array
  p50: Float32Array
  p75: Float32Array
  p90: Float32Array
  required: Float32Array

  targetIndex: number
  /**
   * From `fanYMax`, shared with the app's chart. Carried on the model rather than
   * recomputed in the painter so there is exactly one call site and the card
   * cannot end up drawn to a different scale from the app.
   */
  yMax: number
}

/**
 * Builds a card model from a settled simulation result.
 *
 * Takes the raw `SimResult`, never `useAnimatedFan`'s `FanFrame`: the frame is
 * mid-interpolation for a few hundred milliseconds after every slider move, and a
 * card rendered from it would freeze the fan halfway through its ease.
 *
 * **The target age comes out of the result, not from the caller.** It used to be a
 * parameter, and that was a bug with a very bad failure mode: the app holds the
 * last settled result while the sliders keep moving, so passing the live
 * `fireByAge` alongside a settled result produced a card whose eyebrow read "by
 * 55" over a probability computed for 45. `ages[targetIndex]` is the age this
 * result was actually run for, so the two cannot disagree.
 */
export function cardModelFrom(result: SimResult, theme: CardTheme): CardModel {
  const index = Math.min(Math.max(0, result.targetIndex), result.ages.length - 1)
  return {
    probability: result.probabilityAtTarget,
    targetAge: result.ages.length === 0 ? 0 : result.ages[index],
    theme,
    ages: result.ages,
    p10: result.bands.p10,
    p25: result.bands.p25,
    p50: result.bands.p50,
    p75: result.bands.p75,
    p90: result.bands.p90,
    required: result.requiredMedian,
    targetIndex: result.targetIndex,
    yMax: fanYMax(result),
  }
}

/**
 * The same model with the probability suppressed, for the static link-preview
 * image. Everything else — the fan, the scale — is unchanged, so the og:image is
 * recognisably the same product without making a claim.
 */
export function withoutProbability(model: CardModel): CardModel {
  return { ...model, probability: null }
}
